import { FC } from 'react';
import linkifyStr from 'linkify-string';
import DOMPurify from 'dompurify';
import { Box, Flex, Image, Modal, ModalOverlay, ModalContent, ModalCloseButton, ModalHeader, ModalBody, ModalFooter, Text } from '@chakra-ui/react';
import { CbLink } from '../CbLink';
import styles from './CbRewardRedemptionModal.module.css';
export interface CbRewardRedemptionModalProps {
  isLoading: boolean;
  isOpen: boolean;
  onClose: () => void;
  imageUrl?: string;
  rewardHistoryItem: {
    sourceImageUrl: string;
    sourceName: string;
    redemptionInformation: {
      isCompanyReward: boolean;
      deliveryInstructions?: string;
      fulfilledAt?: string;
      email?: string;
      link?: string;
    };
  };
}
export const CbRewardRedemptionModal: FC<CbRewardRedemptionModalProps> = ({
  isLoading,
  isOpen,
  imageUrl,
  onClose,
  rewardHistoryItem
}) => {
  const handleRedemptionLinkClick = () => {
    window.open(rewardHistoryItem.redemptionInformation.link, '_blank', 'noopener,noreferrer');
  };
  const renderArcadeRewardInformation = () => <Box data-sentry-element="Box" data-sentry-component="renderArcadeRewardInformation" data-sentry-source-file="CbRewardRedemptionModal.tsx">
      <Flex marginBottom={2} data-sentry-element="Flex" data-sentry-source-file="CbRewardRedemptionModal.tsx">
        <Text data-sentry-element="Text" data-sentry-source-file="CbRewardRedemptionModal.tsx">{'Purchase Successful'}</Text>
      </Flex>
      <Text fontSize="sm" data-sentry-element="Text" data-sentry-source-file="CbRewardRedemptionModal.tsx">
        {link('Click here')}
        {` to redeem your reward!`}
      </Text>
    </Box>;
  const renderCompanyRewardInformation = () => <Box data-sentry-element="Box" data-sentry-component="renderCompanyRewardInformation" data-sentry-source-file="CbRewardRedemptionModal.tsx">
      <Flex marginBottom={2} data-sentry-element="Flex" data-sentry-source-file="CbRewardRedemptionModal.tsx">
        <Text data-sentry-element="Text" data-sentry-source-file="CbRewardRedemptionModal.tsx">{'Awaiting Delivery'}</Text>
      </Flex>
      <Text fontSize="sm" data-sentry-element="Text" data-sentry-source-file="CbRewardRedemptionModal.tsx">
        This reward is delivered by your company and you will receive a
        notification when it has been marked as delivered.
      </Text>
    </Box>;
  const renderDeliveredCompanyReward = deliveryInstructions => {
    return <Box data-sentry-element="Box" data-sentry-component="renderDeliveredCompanyReward" data-sentry-source-file="CbRewardRedemptionModal.tsx">
        <Flex marginBottom={2} data-sentry-element="Flex" data-sentry-source-file="CbRewardRedemptionModal.tsx">
          <Text data-sentry-element="Text" data-sentry-source-file="CbRewardRedemptionModal.tsx">{'Reward Delivered'}</Text>
        </Flex>
        <Text fontSize="sm" data-sentry-element="Text" data-sentry-source-file="CbRewardRedemptionModal.tsx">
          This reward has been marked as delivered by your manager. Please
          contact them if you have any issues with your reward.
        </Text>
        {!!deliveryInstructions && <Box>
            <Text fontSize="sm" marginTop={2}>
              The following instructions have been provided relating to your
              reward:
            </Text>
            <p className={styles.text} dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(linkifyStr(deliveryInstructions, {
            className: styles.clickableLink
          }))
        }}></p>
          </Box>}
      </Box>;
  };
  const renderModalContent = () => {
    if (!isOpen) return null;
    if (isLoading) {
      return <p>Loading</p>;
    }
    const {
      isCompanyReward,
      deliveryInstructions,
      fulfilledAt
    } = rewardHistoryItem.redemptionInformation;
    if (isCompanyReward) {
      return !!fulfilledAt ? renderDeliveredCompanyReward(deliveryInstructions) : renderCompanyRewardInformation();
    }
    return renderArcadeRewardInformation();
  };
  const link = (text: string) => <CbLink as="a" onClick={handleRedemptionLinkClick} fontWeight="bold" textDecoration="underline" color="chonky.500" target="_blank" data-sentry-element="CbLink" data-sentry-component="link" data-sentry-source-file="CbRewardRedemptionModal.tsx">
      {text}
    </CbLink>;
  return <Modal isCentered isOpen={isOpen} onClose={onClose} data-sentry-element="Modal" data-sentry-component="CbRewardRedemptionModal" data-sentry-source-file="CbRewardRedemptionModal.tsx">
      <ModalOverlay data-sentry-element="ModalOverlay" data-sentry-source-file="CbRewardRedemptionModal.tsx" />
      <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="CbRewardRedemptionModal.tsx">
        <ModalCloseButton variant="cb-ghost" data-sentry-element="ModalCloseButton" data-sentry-source-file="CbRewardRedemptionModal.tsx" />
        <ModalHeader data-sentry-element="ModalHeader" data-sentry-source-file="CbRewardRedemptionModal.tsx">{rewardHistoryItem?.sourceName || 'Redeem'}</ModalHeader>
        <ModalBody data-sentry-element="ModalBody" data-sentry-source-file="CbRewardRedemptionModal.tsx">
          <Image data-testid="redemption-image" height={20} borderRadius={8} marginBottom={8} src={imageUrl || rewardHistoryItem?.sourceImageUrl} data-sentry-element="Image" data-sentry-source-file="CbRewardRedemptionModal.tsx" />
          <Box data-sentry-element="Box" data-sentry-source-file="CbRewardRedemptionModal.tsx">{renderModalContent()}</Box>
        </ModalBody>
        <ModalFooter data-sentry-element="ModalFooter" data-sentry-source-file="CbRewardRedemptionModal.tsx"></ModalFooter>
      </ModalContent>
    </Modal>;
};